import { Search } from 'lucide-react'
import type React from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PanelSuggestionSkeleton } from '@/components/ui/skeleton'
import { useLanguage } from '@/landing/contexts/LanguageContext'
import type { SearchByNameProps } from '@/types/components/landing'
import { getPanelImage } from '@/utils/searchUtils'

export const SearchByName: React.FC<SearchByNameProps> = ({
  productName,
  showSuggestions,
  selectedIndex,
  suggestions,
  nameSearchLoading,
  searchContainerRef,
  handlers,
  onFocus,
  onSelectedIndexChange,
  searchError,
}) => {
  const { t } = useLanguage()

  return (
    <div className="mb-12">
      <h2
        className="text-3xl font-bold text-gray-900 mb-2"
        style={{
          fontFamily: 'Montserrat, sans-serif',
        }}
      >
        {t('main_page.search.by_name.search_by')}{' '}
        <span className="text-primary">{t('main_page.search.by_name.search_term')}</span>
      </h2>
      <p className="text-gray-600 mb-6">{t('main_page.search.by_name.description')}</p>
      <div className="flex gap-4 items-center">
        <div className="flex-1 relative" ref={searchContainerRef}>
          <Input
            type="text"
            value={productName}
            onChange={(e) => handlers.handleInputChange(e.target.value)}
            onKeyDown={handlers.handleKeyDown}
            onFocus={onFocus}
            placeholder={t('main_page.search.by_name.placeholder')}
            className="text-lg py-6 px-4"
          />

          {showSuggestions && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
              {nameSearchLoading ? (
                Array.from({ length: 3 }).map((_, i) => <PanelSuggestionSkeleton key={i} />)
              ) : suggestions.length > 0 ? (
                suggestions.map((panel, index) => (
                  <button
                    key={panel.id}
                    type="button"
                    onClick={() => handlers.selectSuggestion(panel)}
                    onMouseEnter={() => onSelectedIndexChange(index)}
                    className={`w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors ${
                      index === selectedIndex ? 'bg-gray-50' : ''
                    }`}
                  >
                    {getPanelImage(panel) ? (
                      <img
                        src={getPanelImage(panel)}
                        alt={panel.name}
                        className="w-10 h-10 object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center text-xs font-bold text-gray-600">
                        {panel.name?.substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 text-left">
                      <div className="font-medium text-gray-900">{panel.name}</div>
                      {panel.name_imperial !== panel.name && (
                        <div className="text-sm text-gray-500">{panel.name_imperial}</div>
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500 text-sm">
                  {t('main_page.search.by_name.no_results')}
                </div>
              )}
            </div>
          )}
        </div>
        <Button
          onClick={handlers.handleProductSearch}
          disabled={!productName.trim()}
          size="lg"
          className="p-6 md:px-8"
        >
          <Search className="w-5 h-5 md:mr-2" />
          <span className="hidden md:inline">{t('main_page.search.by_name.button')}</span>
        </Button>
      </div>
      {searchError && <div className="mt-2 text-sm text-red-600">{t(searchError)}</div>}
    </div>
  )
}
